/**
 * PcsBuddyAssistPrompt — page-aware PCS Buddy prompt.
 *
 * Rail routes (/listings, /locations, /locations/:id, /location-match):
 *   Desktop: fixed right-side vertical tab that expands into a side panel.
 *   Mobile:  small FAB at bottom-left that opens a slide-up bottom sheet.
 *
 * All other routes:
 *   Bottom-left compact card that expands in place — unchanged from prior design.
 *
 * Positioned bottom-left (non-rail) / right-edge (rail desktop).
 * FloatingFavorites is bottom-right — no overlap.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X, Sparkles, ChevronDown, ArrowRight, ChevronLeft } from 'lucide-react';
import { usePcsBuddyAssist } from '../hooks/usePcsBuddyAssist';
import { useLanguage } from '../context/LanguageContext';

export default function PcsBuddyAssistPrompt() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { visible, promptConfig, dismiss, openConcierge, expanded, expand, collapse, isRailRoute } =
    usePcsBuddyAssist();

  const brandLabel = t('concierge.brand_name', 'PCS Buddy');

  const [mounted, setMounted] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      const t = setTimeout(() => setAnimateIn(true), 30);
      return () => clearTimeout(t);
    } else {
      setAnimateIn(false);
      const t = setTimeout(() => setMounted(false), 350);
      return () => clearTimeout(t);
    }
  }, [visible]);

  if (!mounted || !promptConfig) return null;

  // ── Rail layout (desktop sm+, mobile FAB) ──────────────────────────────────
  if (isRailRoute) {
    return (
      <>
        {/* Desktop rail — hidden on mobile */}
        <div
          className={`
            hidden sm:flex fixed right-0 top-1/2 -translate-y-1/2 z-40
            flex-row-reverse
            transition-all duration-300 ease-out
            ${animateIn ? 'opacity-100 translate-x-0' : 'opacity-100 translate-x-2'}
          `}
          role="complementary"
          aria-label={t('pcs_buddy.aria.assist_rail', 'PCS Buddy assist rail')}
          data-testid="pcs-buddy-rail"
        >
          {/* Tab handle — always visible on desktop */}
          <button
            onClick={expanded ? collapse : expand}
            className="w-9 bg-signal-blue hover:bg-signal-blue/90 rounded-l-xl flex flex-col items-center justify-center gap-3 py-8 shadow-lg transition-colors flex-shrink-0"
            aria-label={expanded ? t('pcs_buddy.aria.collapse', 'Collapse PCS Buddy') : t('pcs_buddy.aria.open', 'Open PCS Buddy')}
          >
            <Sparkles className="w-4 h-4 text-white flex-shrink-0" />
            <span
              className="text-white text-[9px] font-bold tracking-widest uppercase select-none"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {brandLabel}
            </span>
            <ChevronLeft
              className={`w-3.5 h-3.5 text-white/70 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Expanded side panel */}
          {expanded && (
            <div className="w-72 bg-white border border-r-0 border-slate-200 rounded-l-xl shadow-xl flex flex-col max-h-[70vh]">
              {/* Header */}
              <div className="px-4 py-3 bg-signal-blue/5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-signal-blue/10 rounded-full flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-signal-blue" />
                  </div>
                  <span className="text-xs font-semibold text-signal-blue tracking-wide uppercase">
                    {brandLabel}
                  </span>
                </div>
                <button
                  onClick={dismiss}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                  aria-label={t('pcs_buddy.aria.dismiss', 'Dismiss')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Content */}
              <div className="px-4 pt-3 pb-4 overflow-y-auto flex-1">
                <p className="text-sm font-semibold text-slate-800 mb-2">{promptConfig.headline}</p>

                {promptConfig.expandedGuidance && (
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    {promptConfig.expandedGuidance}
                  </p>
                )}

                {promptConfig.suggestedQuestions?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">
                      {t('pcs_buddy.suggested_questions', 'Suggested questions')}
                    </p>
                    <div className="space-y-1.5">
                      {promptConfig.suggestedQuestions.map((question, i) => (
                        <button
                          key={i}
                          onClick={() => openConcierge(navigate, question)}
                          className="w-full text-left px-3 py-2 text-xs text-slate-700 bg-slate-50 hover:bg-signal-blue/5 hover:text-signal-blue border border-slate-200 hover:border-signal-blue/30 rounded-lg transition-colors"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => openConcierge(navigate)}
                  className="flex items-center gap-1 text-xs text-signal-blue hover:underline"
                >
                  {t('pcs_buddy.open_full_chat', 'Open full chat')}
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile FAB + bottom sheet — visible only on mobile */}
        <div
          className={`
            sm:hidden fixed bottom-20 left-4 z-40
            transition-all duration-300 ease-out
            ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
          `}
          data-testid="pcs-buddy-mobile-fab"
        >
          {!expanded ? (
            <button
              onClick={expand}
              className="w-12 h-12 bg-signal-blue hover:bg-signal-blue/90 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
              aria-label={t('pcs_buddy.aria.open', 'Open PCS Buddy')}
            >
              <MessageSquare className="w-5 h-5" />
            </button>
          ) : (
            /* Bottom sheet overlay */
            <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl flex flex-col max-h-[65vh]">
              <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>
              <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-signal-blue" />
                  <span className="text-sm font-semibold text-signal-blue">{brandLabel}</span>
                </div>
                <button onClick={collapse} className="p-1 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-4 py-3 overflow-y-auto flex-1">
                <p className="text-sm font-semibold text-slate-800 mb-2">{promptConfig.headline}</p>
                {promptConfig.expandedGuidance && (
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    {promptConfig.expandedGuidance}
                  </p>
                )}
                {promptConfig.suggestedQuestions?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {promptConfig.suggestedQuestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => openConcierge(navigate, q)}
                        className="w-full text-left px-3 py-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg hover:bg-signal-blue/5 hover:text-signal-blue transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => openConcierge(navigate)}
                  className="flex items-center gap-1 text-xs text-signal-blue hover:underline"
                >
                  {t('pcs_buddy.open_full_chat', 'Open full chat')} <ArrowRight className="w-3 h-3" />
                </button>
                <button
                  onClick={dismiss}
                  className="mt-4 text-xs text-slate-400 hover:text-slate-600 block"
                >
                  {t('pcs_buddy.dismiss', 'Dismiss')}
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // ── Default bottom-left card (non-rail routes) ─────────────────────────────
  return (
    <div
      className={`
        fixed bottom-20 left-4 sm:left-6 z-40
        transition-all duration-300 ease-out
        ${expanded ? 'w-[calc(100vw-2rem)] sm:w-96 max-w-md' : 'w-[calc(100vw-2rem)] sm:w-80 max-w-sm'}
        ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      role="complementary"
      aria-label={t('pcs_buddy.aria.assist', 'PCS Buddy assist')}
      data-testid="pcs-buddy-assist-prompt"
    >
      <div className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-[calc(100vh-8rem)] flex flex-col">

        {/* Header — always visible */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-signal-blue/5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-signal-blue/10 rounded-full flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-signal-blue" />
            </div>
            <span className="text-xs font-semibold text-signal-blue tracking-wide uppercase">
              {brandLabel}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            {expanded && (
              <button
                onClick={collapse}
                className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                aria-label={t('pcs_buddy.aria.collapse_panel', 'Collapse')}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={dismiss}
              className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
              aria-label={t('pcs_buddy.aria.dismiss', 'Dismiss')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {expanded ? (
          /* ── Expanded panel ── */
          <div className="px-4 pt-3 pb-4 overflow-y-auto">
            <p className="text-sm font-semibold text-slate-800 mb-2">
              {promptConfig.headline}
            </p>

            {promptConfig.expandedGuidance && (
              <p className="text-xs text-slate-600 leading-relaxed mb-3">
                {promptConfig.expandedGuidance}
              </p>
            )}

            {promptConfig.suggestedQuestions?.length > 0 && (
              <div className="mb-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">
                  {t('pcs_buddy.suggested_questions', 'Suggested questions')}
                </p>
                <div className="space-y-1.5">
                  {promptConfig.suggestedQuestions.map((question, i) => (
                    <button
                      key={i}
                      onClick={() => openConcierge(navigate, question)}
                      className="w-full text-left px-3 py-2 text-xs text-slate-700 bg-slate-50 hover:bg-signal-blue/5 hover:text-signal-blue border border-slate-200 hover:border-signal-blue/30 rounded-lg transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <button
                onClick={() => openConcierge(navigate)}
                className="flex items-center gap-1 text-xs text-signal-blue hover:underline"
              >
                {t('pcs_buddy.open_full_chat', 'Open full chat')}
                <ArrowRight className="w-3 h-3" />
              </button>
              <button
                onClick={dismiss}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                {t('pcs_buddy.close', 'Close')}
              </button>
            </div>
          </div>
        ) : (
          /* ── Compact card ── */
          <div className="px-4 pt-3 pb-4">
            <p className="text-sm font-semibold text-slate-800 mb-1">
              {promptConfig.headline}
            </p>
            <p className="text-xs text-slate-600 leading-relaxed mb-4">
              {promptConfig.body}
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={expand}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-signal-blue hover:bg-signal-blue/90 text-white text-xs font-semibold rounded-lg transition-colors"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {promptConfig.cta}
              </button>
              <button
                onClick={dismiss}
                className="px-3 py-2 text-slate-500 hover:text-slate-700 text-xs rounded-lg transition-colors"
              >
                {t('pcs_buddy.not_now', 'Not now')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
